import { useState, FormEvent } from 'react';
import { useNavigate, Navigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verify2FA, isLoading, isAuthenticated } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/dashboard';
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) {
      setError(t('auth.emailPasswordRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const result = await login(email.trim(), password);
      if (result.reloading) return; // Run N-5 — a stale-bundle reload is in flight; stay put
      if (result.requires2FA && result.tempToken) {
        setShow2FA(true);
        setTempToken(result.tempToken);
        setSubmitting(false);
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.loginFailed'));
    }
    setSubmitting(false);
  };

  const handle2FASubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');
    if (!twoFACode.trim()) {
      setError(t('auth.2faCodeRequired', 'Please enter your 2FA code'));
      return;
    }
    setSubmitting(true);
    try {
      const result = await verify2FA(tempToken, twoFACode.trim());
      if (result.reloading) return; // Run N-5
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.2faFailed', '2FA verification failed'));
    }
    setSubmitting(false);
  };

  const inputStyle = {
    width: '100%', height: '44px', border: '0.5px solid #d6cfc4',
    borderRadius: '7px', padding: '0 14px', fontSize: '14px',
    color: '#2c2c2a', background: '#fff', marginBottom: '16px',
    boxSizing: 'border-box' as const, outline: 'none',
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = '#2c2c2a';
    e.target.style.boxShadow = '0 0 0 2px rgba(44,44,42,0.08)';
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = '#d6cfc4';
    e.target.style.boxShadow = 'none';
  };

  return (
    <div style={{
      background: '#f5f5f0', minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Inter', system-ui, sans-serif", padding: '20px',
    }}>
      <div style={{
        background: '#fff', border: '0.5px solid #e2ddd5', borderRadius: '12px',
        padding: '40px 36px', width: '100%', maxWidth: '400px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        {/* Logo */}
        {/* Brand mark — Night 8: match the sidebar's "MI" text-in-square
            pattern so the brand glyph is the same whether the user is
            logged in or logged out. The login page previously used a
            sun/sparkle SVG which read as a different brand mark next to
            the same wordmark in the sidebar.
            Sidebar source of truth: frontend/src/components/layout/Sidebar.tsx
            lines 114-116 — 7×7 charcoal-900 square, [9px] charcoal-50 "MI". */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '7px', background: '#2c2c2a',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: '11px', fontWeight: 500, color: '#f5f5f0', letterSpacing: '0.3px' }}>
              MI
            </span>
          </div>
          <span style={{ fontSize: '18px', fontWeight: 500, color: '#2c2c2a', letterSpacing: '-0.3px' }}>
            MSK Insight
          </span>
          <span style={{
            fontSize: '9px', color: '#8a8478', padding: '1px 5px',
            border: '0.5px solid #c4bdb2', borderRadius: '4px',
          }}>v2</span>
        </div>

        {/* Subtitle */}
        <p style={{ fontSize: '13px', color: '#6b665e', marginBottom: '28px' }}>
          {show2FA
            ? t('auth.2faSubtitle', 'Enter the 6-digit code from your authenticator app')
            : t('auth.signInSubtitle', 'Sign in to your account')}
        </p>

        {/* Error */}
        {error && (
          <div style={{
            fontSize: '12px', color: '#b91c1c', background: '#fee2e2',
            padding: '10px 14px', borderRadius: '7px', marginBottom: '16px',
            border: '0.5px solid #fecaca',
          }}>
            {error}
          </div>
        )}

        {!show2FA ? (
          <>
          {/* OAuth buttons */}
          <div style={{ marginBottom: '16px' }}>
            <button type="button" onClick={() => setError('Google Sign-In requires configuration. Use email login.')} style={{
              width: '100%', height: '44px', border: '0.5px solid #d6cfc4', borderRadius: '7px',
              background: '#fff', fontSize: '14px', fontWeight: 500, color: '#2c2c2a',
              cursor: 'pointer', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </button>
            <button type="button" onClick={() => setError('Apple Sign-In requires configuration. Use email login.')} style={{
              width: '100%', height: '44px', border: '0.5px solid #d6cfc4', borderRadius: '7px',
              background: '#fff', fontSize: '14px', fontWeight: 500, color: '#2c2c2a',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            }}>
              <svg width="16" height="18" viewBox="0 0 17 20" fill="currentColor"><path d="M13.86 10.5c-.02-2.01 1.64-2.97 1.72-3.02-.94-1.37-2.4-1.56-2.92-1.58-1.24-.13-2.43.73-3.06.73-.63 0-1.6-.71-2.64-.69-1.36.02-2.61.79-3.31 2.01-1.41 2.45-.36 6.08 1.02 8.07.67.97 1.47 2.06 2.53 2.02 1.01-.04 1.4-.66 2.62-.66 1.23 0 1.57.66 2.63.64 1.09-.02 1.79-.99 2.45-1.97.77-1.12 1.09-2.21 1.11-2.27-.02-.01-2.13-.82-2.15-3.28zM11.87 4.18c.56-.68.94-1.62.83-2.56-.81.03-1.78.54-2.36 1.22-.52.6-.97 1.56-.85 2.48.9.07 1.82-.46 2.38-1.14z"/></svg>
              Continue with Apple
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1, height: '0.5px', background: '#d6cfc4' }} />
            <span style={{ fontSize: '12px', color: '#a39d94' }}>or</span>
            <div style={{ flex: 1, height: '0.5px', background: '#d6cfc4' }} />
          </div>

          <form onSubmit={handleSubmit}>
            <label style={{ fontSize: '12px', fontWeight: 500, color: '#6b665e', display: 'block', marginBottom: '6px' }}>
              {t('auth.emailLabel', 'Email')}
            </label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder', 'you@clinic.com')}
              autoComplete="email" autoFocus style={inputStyle}
              onFocus={handleFocus} onBlur={handleBlur}
            />

            <label style={{ fontSize: '12px', fontWeight: 500, color: '#6b665e', display: 'block', marginBottom: '6px' }}>
              {t('auth.password', 'Password')}
            </label>
            <input
              type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder={t('auth.passwordPlaceholder', 'Enter your password')}
              autoComplete="current-password" style={inputStyle}
              onFocus={handleFocus} onBlur={handleBlur}
            />

            <button type="submit" disabled={submitting || isLoading} style={{
              width: '100%', height: '44px', background: submitting ? '#8a8478' : '#2c2c2a',
              color: '#f5f5f0', border: 'none', borderRadius: '8px',
              fontSize: '14px', fontWeight: 500, cursor: submitting ? 'not-allowed' : 'pointer',
              marginTop: '8px', transition: 'background 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            }}>
              {submitting && <Spinner />}
              {submitting ? t('auth.signingIn', 'Signing in...') : t('auth.signIn', 'Sign in')}
            </button>
          </form>

          <p style={{ textAlign: 'center', fontSize: '13px', color: '#6b665e', marginTop: '16px' }}>
            Don't have an account?{' '}
            <Link to="/signup" style={{ color: '#2c2c2a', fontWeight: 500, textDecoration: 'none' }}>Sign up</Link>
          </p>
          </>
        ) : (
          <form onSubmit={handle2FASubmit}>
            <label style={{ fontSize: '12px', fontWeight: 500, color: '#6b665e', display: 'block', marginBottom: '6px' }}>
              {t('auth.2faCode', 'Authentication Code')}
            </label>
            <input
              type="text" value={twoFACode} onChange={e => setTwoFACode(e.target.value.replace(/[^0-9A-Za-z-]/g, ''))}
              placeholder="000000"
              autoComplete="one-time-code" autoFocus
              maxLength={10}
              style={{ ...inputStyle, textAlign: 'center', fontSize: '20px', letterSpacing: '4px' }}
              onFocus={handleFocus} onBlur={handleBlur}
            />
            <p style={{ fontSize: '11px', color: '#8a8478', marginBottom: '16px', marginTop: '-8px' }}>
              {t('auth.2faBackupHint', 'You can also use a backup code (format: XXXX-XXXX)')}
            </p>

            <button type="submit" disabled={submitting} style={{
              width: '100%', height: '44px', background: submitting ? '#8a8478' : '#2c2c2a',
              color: '#f5f5f0', border: 'none', borderRadius: '8px',
              fontSize: '14px', fontWeight: 500, cursor: submitting ? 'not-allowed' : 'pointer',
              marginTop: '8px', transition: 'background 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            }}>
              {submitting && <Spinner />}
              {submitting ? t('auth.verifying', 'Verifying...') : t('auth.verify', 'Verify')}
            </button>

            <button type="button" onClick={() => { setShow2FA(false); setTwoFACode(''); setError(''); }} style={{
              width: '100%', height: '36px', background: 'transparent', color: '#6b665e',
              border: '0.5px solid #d6cfc4', borderRadius: '7px', fontSize: '13px',
              cursor: 'pointer', marginTop: '8px',
            }}>
              {t('auth.backToLogin', 'Back to login')}
            </button>
          </form>
        )}

        {/* Clinic name */}
        <div style={{
          textAlign: 'center' as const, fontSize: '10px', color: '#a39d94',
          marginTop: '24px', paddingTop: '16px',
          borderTop: '0.5px solid #eeeeea',
        }}>
          The Chiropractic Center
        </div>
      </div>

      {/* Page footer */}
      <div style={{
        position: 'fixed' as const, bottom: '0', left: '0', right: '0',
        textAlign: 'center' as const, fontSize: '10px', color: '#c4bdb2', padding: '16px',
      }}>
        Powered by MSK Insight
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <span style={{
      width: '16px', height: '16px', borderRadius: '50%',
      border: '2px solid #f5f5f0', borderTopColor: 'transparent',
      animation: 'spin 0.6s linear infinite', display: 'inline-block',
    }} />
  );
}
